import { useEffect, useState } from "react";
import { X, Megaphone, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * AnnouncementBar — shows the most recent ACTIVE announcement above the
 * dashboard. Devs push announcements via the Admin panel
 * (`/api/admin/announcements`). Users can dismiss; dismissals are
 * persisted server-side per user.
 */
const SEVERITY = {
  info:    { bg: "bg-[#5A90F5]/15",  text: "text-[#5A90F5]",  border: "border-[#5A90F5]/40" },
  success: { bg: "bg-[#34C759]/15",  text: "text-[#34C759]",  border: "border-[#34C759]/40" },
  warn:    { bg: "bg-[#FF9F0A]/15",  text: "text-[#FF9F0A]",  border: "border-[#FF9F0A]/40" },
  danger:  { bg: "bg-[#FF3B30]/15",  text: "text-[#FF3B30]",  border: "border-[#FF3B30]/40" },
};

export function AnnouncementBar() {
  const { user } = useAuth();
  const [ann, setAnn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) { setAnn(null); return; }

    const load = async () => {
      // Check dev announcements first (higher priority), then org announcements
      try {
        const { data } = await api.get("/announcements/active");
        if (!cancelled && data) { setAnn(data); return; }
      } catch {}
      // Fall back to org announcement if no dev announcement
      try {
        const { data } = await api.get("/me/org-announcement-active");
        if (!cancelled) setAnn(data || null);
      } catch {}
    };

    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (!ann) return null;

  const dismiss = () => {
    api.post(`/announcements/dismiss/${ann.announcement_id}`).catch(() => {});
    setAnn(null);
  };

  // Card-type announcements show as a centered modal, not a bar
  if (ann.type === "card") {
    return (
      <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/60 p-6">
        <div className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-[#2A2A2A] shadow-2xl"
          style={{ background: ann.bg_color || "#0D0D0D" }}>
          {/* X */}
          <button onClick={dismiss}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center text-white/60 hover:text-white z-10">
            <X className="w-3.5 h-3.5"/>
          </button>
          {/* Two-panel layout */}
          <div className="flex min-h-[200px]">
            <div className="flex-1 p-7 flex flex-col justify-between border-r border-white/5">
              {ann.image_url && <img src={ann.image_url} alt="" className="w-full rounded-lg mb-4 object-cover" style={{ maxHeight: 100 }}/>}
              <div>
                <h2 className="font-bold text-[18px] text-white mb-1 leading-tight">{ann.title || ann.message}</h2>
                {ann.subtitle && <p className="text-[12px] mt-1" style={{ color: `${ann.accent_color || "#5A90F5"}cc` }}>{ann.subtitle}</p>}
              </div>
            </div>
            <div className="flex-1 p-7 flex flex-col justify-between">
              {ann.body && <p className="text-[12px] text-[#A1A1AA] leading-relaxed flex-1">{ann.body}</p>}
              <div className="flex justify-end mt-6">
                {ann.cta_label && ann.cta_url ? (
                  <a href={ann.cta_url} target="_blank" rel="noreferrer"
                    className="px-5 py-2 rounded-xl text-[13px] font-semibold text-white inline-block"
                    style={{ background: ann.accent_color || "#5A90F5" }}>
                    {ann.cta_label}
                  </a>
                ) : (
                  <button onClick={dismiss}
                    className="px-5 py-2 rounded-xl text-[13px] font-semibold text-white"
                    style={{ background: ann.accent_color || "#5A90F5" }}>
                    Got it
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sev = SEVERITY[ann.severity] || SEVERITY.info;

  return (
    <div
      className={`w-full flex items-center gap-3 px-4 py-2 border-b ${sev.bg} ${sev.border}`}
      data-testid="announcement-bar"
      data-cresc-topbar
    >
      <Megaphone className={`w-3.5 h-3.5 flex-shrink-0 ${sev.text}`} />
      <div className={`flex-1 text-xs ${sev.text}`} data-testid="announcement-bar-message">
        {ann.message}
      </div>
      {ann.cta_label && ann.cta_url && (
        <a
          href={ann.cta_url}
          target={/^https?:\/\//.test(ann.cta_url) ? "_blank" : "_self"}
          rel="noreferrer"
          className={`text-xs font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 hover:underline ${sev.text}`}
          data-testid="announcement-bar-cta"
        >
          {ann.cta_label}
          {/^https?:\/\//.test(ann.cta_url) && <ExternalLink className="w-3 h-3" />}
        </a>
      )}
      <button
        onClick={dismiss}
        className={`p-0.5 rounded hover:bg-black/20 ${sev.text}`}
        title="Dismiss"
        data-testid="announcement-bar-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
